import type {
  ManagerialDashboardMeasurements,
  ManagerialDashboardMeasurement,
} from '@/types/api'
import {
  formatFullCurrencyBrl,
  formatPercentDisplay,
  formatIntegerBr,
} from './managerial-kpis'

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

export function isMeasurements(value: unknown): value is ManagerialDashboardMeasurements {
  if (!record(value)) return false
  const date = /^\d{4}-\d{2}-\d{2}$/
  if (typeof value.date_from !== 'string' || !date.test(value.date_from)) return false
  if (typeof value.date_to !== 'string' || !date.test(value.date_to)) return false
  if (!Array.isArray(value.sections) || value.sections.length > 20) return false
  return value.sections.every((section: unknown) => {
    if (
      !record(section) ||
      !Array.isArray(section.metrics) ||
      section.metrics.length > 50
    )
      return false
    return (
      ['id', 'title', 'period'].every((key) => typeof section[key] === 'string') &&
      section.metrics.every((metric: unknown) => {
        if (!record(metric)) return false
        return (
          ['id', 'label', 'note', 'source'].every(
            (key) => typeof metric[key] === 'string'
          ) &&
          typeof metric.unit === 'string' &&
          ['currency', 'percent', 'integer', 'decimal'].includes(metric.unit) &&
          (metric.value === null ||
            (typeof metric.value === 'number' &&
              Number.isFinite(metric.value) &&
              (metric.unit !== 'integer' ||
                (Number.isInteger(metric.value) && metric.value >= 0))))
        )
      })
    )
  })
}

export function formatMeasurement(metric: ManagerialDashboardMeasurement): string {
  if (metric.value === null) return '—'
  switch (metric.unit) {
    case 'currency':
      return formatFullCurrencyBrl(metric.value)
    case 'percent':
      return formatPercentDisplay(metric.value)
    case 'integer':
      return formatIntegerBr(metric.value)
    case 'decimal':
      return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(
        metric.value
      )
  }
}
